import React, { useState } from 'react';
import { Clock, Github, Rocket } from 'lucide-react';
interface Step {
  number: number;
  title: string;
  color: string;
  bgColor: string;
  lineColor: string;
  hoverColor: string;
  hoverBg: string;
}
const steps: Step[] = [{
  number: 1,
  title: 'Quick Feasibility Check',
  color: 'text-blue-600',
  bgColor: 'bg-blue-50',
  lineColor: 'bg-blue-100',
  hoverColor: 'text-white',
  hoverBg: 'bg-blue-500'
}, {
  number: 2,
  title: 'Project Scoping',
  color: 'text-purple-600',
  bgColor: 'bg-purple-50',
  lineColor: 'bg-purple-100',
  hoverColor: 'text-white',
  hoverBg: 'bg-purple-500'
}, {
  number: 3,
  title: 'Consultation & Planning',
  color: 'text-green-600',
  bgColor: 'bg-green-50',
  lineColor: 'bg-green-100',
  hoverColor: 'text-white',
  hoverBg: 'bg-green-500'
}, {
  number: 4,
  title: 'Development Phase',
  color: 'text-red-600',
  bgColor: 'bg-red-50',
  lineColor: 'bg-red-100',
  hoverColor: 'text-white',
  hoverBg: 'bg-red-500'
}, {
  number: 5,
  title: 'Market-Ready',
  color: 'text-teal-600',
  bgColor: 'bg-teal-50',
  lineColor: 'bg-teal-100',
  hoverColor: 'text-white',
  hoverBg: 'bg-teal-500'
}];
export function JourneyTimeline() {
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  return <section id="how-it-works" className="py-24 bg-white dark:bg-gray-900" data-id="element-230">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-231">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-232">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-233">
            Your Journey to MVP
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-234">
            We've streamlined the development process into clear steps, taking
            you from idea to market-ready product in just 4 weeks
          </p>
        </div>

        {/* Timeline */}
        <div className="relative max-w-4xl mx-auto" data-id="element-235">
          <div className="space-y-12" data-id="element-236">
            {steps.map((step, index) => {
            const isLeft = index % 2 === 0;
            const isLast = index === steps.length - 1;
            const isHovered = hoveredStep === step.number;
            return <div key={step.number} className="relative group cursor-pointer" onMouseEnter={() => setHoveredStep(step.number)} onMouseLeave={() => setHoveredStep(null)} data-id="element-237">
                  {/* Connecting Line */}
                  {!isLast && <div className={`absolute top-16 w-0.5 h-12 ${step.lineColor}`} style={{
                left: isLeft ? '2rem' : 'auto',
                right: isLeft ? 'auto' : '2rem'
              }} data-id="element-238" />}

                  <div className={`flex items-start ${isLeft ? 'flex-row' : 'flex-row-reverse'}`} data-id="element-239">
                    {/* Number Block */}
                    <div className="relative z-10 flex-shrink-0" data-id="element-240">
                      <div className={`
                          w-16 h-16 rounded-2xl flex items-center justify-center shadow-sm transition-all duration-300
                          ${isHovered ? step.hoverBg : step.bgColor}
                        `} data-id="element-241">
                        <span className={`
                            text-2xl font-bold transition-colors duration-300
                            ${isHovered ? step.hoverColor : step.color}
                          `} data-id="element-242">
                          {step.number}
                        </span>
                      </div>
                    </div>

                    {/* Content Area */}
                    <div className={`flex-grow pt-4 ${isLeft ? 'pl-8 text-left' : 'pr-8 text-right'}`} data-id="element-243">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-244">
                        {step.title}
                      </h3>

                      {/* Step 1 Hover Card */}
                      {step.number === 1 && <div className={`
                            bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-6 max-w-sm
                            transition-all duration-300 transform origin-top-left
                            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}
                          `} data-id="element-245">
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-left" data-id="element-246">
                            AI Assessment
                          </h4>
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-247">
                            <div className="flex justify-between items-center mb-2" data-id="element-248">
                              <span className="text-gray-600 dark:text-gray-300 font-medium" data-id="element-249">
                                Technical Feasibility Score:
                              </span>
                              <span className="text-gray-900 dark:text-white font-bold" data-id="element-250">
                                8/10
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5" data-id="element-251">
                              <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-1000 ease-out" style={{
                          width: isHovered ? '80%' : '0%'
                        }} data-id="element-252"></div>
                            </div>
                          </div>
                        </div>}

                      {/* Step 2 Hover Card */}
                      {step.number === 2 && <div className={`
                            bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-6 max-w-sm ml-auto
                            transition-all duration-300 transform origin-top-right
                            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}
                          `} data-id="element-253">
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-left" data-id="element-254">
                            MoSCoW Analysis
                          </h4>
                          <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" data-id="element-255">
                            <span className="bg-red-100 text-red-600 px-3 py-1 rounded text-sm font-medium" data-id="element-256">
                              Must Have
                            </span>
                            <span className="text-gray-900 dark:text-white font-medium" data-id="element-257">
                              User Authentication
                            </span>
                          </div>
                        </div>}

                      {/* Step 3 Hover Card */}
                      {step.number === 3 && <div className={`
                            bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-6 max-w-sm
                            transition-all duration-300 transform origin-top-left
                            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}
                          `} data-id="element-258">
                          <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-2" data-id="element-259">
                            <Clock className="w-5 h-5 text-blue-500" data-id="element-260" />
                            <span className="text-gray-600 dark:text-gray-300 font-medium" data-id="element-261">
                              Technical Consultation
                            </span>
                          </div>
                        </div>}

                      {/* Step 4 Hover Card */}
                      {step.number === 4 && <div className={`
                            bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-6 max-w-sm ml-auto
                            transition-all duration-300 transform origin-top-right
                            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}
                          `} data-id="element-262">
                          <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-2" data-id="element-263">
                            <Github className="w-5 h-5 text-gray-900 dark:text-white" data-id="element-264" />
                            <span className="text-gray-900 dark:text-white font-medium" data-id="element-265">
                              GitHub Project
                            </span>
                          </div>
                        </div>}

                      {/* Step 5 Hover Card */}
                      {step.number === 5 && <div className={`
                            bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-6 max-w-sm
                            transition-all duration-300 transform origin-top-left
                            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}
                          `} data-id="element-266">
                          <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-2" data-id="element-267">
                            <Rocket className="w-5 h-5 text-blue-500" data-id="element-268" />
                            <span className="text-gray-900 dark:text-white font-medium" data-id="element-269">
                              Launch Checklist
                            </span>
                          </div>
                        </div>}
                    </div>

                    {/* Spacer */}
                    <div className="flex-grow hidden md:block" data-id="element-270"></div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}